import { ActionIcon } from '@mantine/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC } from 'react';
import { useShallow } from 'zustand/react/shallow';

import Paragraph from '@/components/text/Paragraph.tsx';

import { DayjsUnit } from '@/models/Dayjs.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { getWeekRangeFromDate } from '@/utils/date-range-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

const DateSwitcher: FC = () => {
    const { is_week_view_enabled, is_weekend_visible } = useCalendarStore(
        useShallow((state) => ({
            is_week_view_enabled: state.is_week_view_enabled,
            is_weekend_visible: state.is_weekend_visible,
        }))
    );

    const { start_date, end_date } = useFilterStore(
        useShallow((state) => ({
            start_date: state.start_date,
            end_date: state.end_date,
        }))
    );

    const navigateBack = (): void => {
        if (!start_date) return;

        if (is_week_view_enabled) {
            // In week view, go back one week
            const new_date = dayjs(start_date).subtract(7, DayjsUnit.DAY).toDate();
            const [start, end] = getWeekRangeFromDate(new_date, is_weekend_visible);

            if (start && end) {
                useFilterStore.setState({
                    start_date: start,
                    end_date: end,
                });
            }
        } else {
            // In day view, go back one day
            const new_date = dayjs(start_date).subtract(1, DayjsUnit.DAY).toDate();

            useFilterStore.setState({
                start_date: new_date,
                end_date: new_date,
            });
        }
    };

    const navigateForward = (): void => {
        if (!start_date) return;

        if (is_week_view_enabled) {
            // In week view, go forward one week
            const new_date = dayjs(start_date).add(7, DayjsUnit.DAY).toDate();
            const [start, end] = getWeekRangeFromDate(new_date, is_weekend_visible);

            if (start && end) {
                useFilterStore.setState({
                    start_date: start,
                    end_date: end,
                });
            }
        } else {
            // In day view, go forward one day
            const new_date = dayjs(start_date).add(1, DayjsUnit.DAY).toDate();

            useFilterStore.setState({
                start_date: new_date,
                end_date: new_date,
            });
        }
    };

    const getFormattedDateDisplay = (): string => {
        if (!start_date) return '';

        if (is_week_view_enabled && end_date) {
            const start = dayjs(start_date);
            const end = dayjs(end_date);
            const week_number = start.week();

            if (start.month() === end.month()) {
                return `${start.format('D')} - ${end.format('D, MMM')} (KW ${week_number})`;
            } else {
                return `${start.format('D.MMM')} - ${end.format('D.MMM')} (KW ${week_number})`;
            }
        } else {
            return dayjs(start_date).format('DD.MMM');
        }
    };

    return (
        <div className="flex items-center gap-2">
            <ActionIcon
                variant="outline"
                size="icon"
                onClick={navigateBack}
                className="h-10 w-10 rounded-full border-gray-300 hover:bg-gray-100"
            >
                <ChevronLeft className="size-5 text-slate-500" />
            </ActionIcon>
            <Paragraph className="w-auto text-center">{getFormattedDateDisplay()}</Paragraph>
            <ActionIcon
                variant="outline"
                size="icon"
                onClick={navigateForward}
                className="h-10 w-10 rounded-full border-gray-300 hover:bg-gray-100"
            >
                <ChevronRight className="size-5 text-gray-500" />
            </ActionIcon>
        </div>
    );
};

export default DateSwitcher;
